import { memo, Fragment } from "react";

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Typography,
  Chip,
} from "@mui/material";
import PropTypes from "prop-types";

import { SO_LINE_ITEM_COLUMNS } from "../constants";

import { getFgAwaitedTone, getFgToneColor } from "../makeData";
import { moneyFormat } from "utils";

const BatchUpdatesList = ({ batchUpdates, theme }) => {
  if (!batchUpdates?.length) return null;

  const batchText = batchUpdates
    .map((batch) => `${batch.batch} × ${batch.qty}`)
    .join(", ");

  return (
    <Typography
      sx={{
        color: theme.palette.text.secondary,
        fontSize: 13,
        whiteSpace: "normal",
        wordBreak: "break-word",
        maxWidth: 200,
      }}
    >
      {batchText}
    </Typography>
  );
};

const SoLineItemTable = ({ order }) => {
  const theme = useTheme();

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1,
          py: 3,
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            letterSpacing: 0.5,
            color: theme.palette.text.secondary,
            fontSize: "12px",
          }}
        >
          LINE ITEMS
        </Typography>
        <Chip
          label={order.lineItems.length}
          size="small"
          sx={{
            height: 17,
            fontSize: 12,
            fontWeight: 700,
            bgcolor: theme.palette.info.lightNavyColor,
            color: theme.palette.primary.main,
          }}
        />
        <Typography
          sx={{
            fontWeight: 600,
            letterSpacing: 0.5,
            color: theme.palette.text.secondary,
            fontSize: "12px",
          }}
        >
          · SELLER ORDER {order?.soNumber}
        </Typography>
      </Box>
      <Box
        sx={{ border: "1px solid", borderColor: "grey.200", borderRadius: 1.5 }}
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {SO_LINE_ITEM_COLUMNS.map((column) => (
                  <TableCell key={column}>
                    <Typography variant="caption" color="text.secondary">
                      {column}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {order.lineItems.map((lineItem, index) => {
                const fgAwaitedTone = getFgAwaitedTone(lineItem);
                return (
                  <Fragment key={lineItem.id}>
                    <TableRow hover>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Chip
                            label={index + 1}
                            size="small"
                            sx={{
                              height: 13,
                              fontSize: 10,
                              fontWeight: 700,
                              bgcolor: theme.palette.info.lightNavyColor,
                              color: theme.palette.primary.main,
                              marginTop: 0.8,
                              borderRadius: "4px",
                              "& .MuiChip-label": {
                                px: "5px",
                              },
                            }}
                          />
                          <Typography variant="body3" fontWeight={600}>
                            {lineItem?.sku}
                          </Typography>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {lineItem.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                          {lineItem.sellerOrderRef}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          color={theme.palette.primary.main}
                        >
                          {lineItem.id}
                        </Typography>
                        <Typography
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: 13,
                          }}
                        >
                          {lineItem.description}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 14 }}>
                          {lineItem.orderQty}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ gap: 2 }}>
                        <Typography sx={{ fontSize: 14 }}>
                          {lineItem.fgReady}
                        </Typography>
                        <BatchUpdatesList
                          batchUpdates={lineItem?.batchUpdates}
                          theme={theme}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight={700}
                          sx={{ color: getFgToneColor(fgAwaitedTone, theme) }}
                        >
                          {lineItem.fgAwaited}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 14 }}>
                          {`${moneyFormat(lineItem?.netValue, 0)}/MT`}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

SoLineItemTable.propTypes = {
  order: PropTypes.shape({
    orderId: PropTypes.string.isRequired,
    lineItems: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        description: PropTypes.string.isRequired,
        orderQty: PropTypes.string.isRequired,
        fgReady: PropTypes.string.isRequired,
        expectedFg: PropTypes.string.isRequired,
        fgAwaited: PropTypes.string.isRequired,
        fgDueBy: PropTypes.string.isRequired,
        eligible: PropTypes.bool.isRequired,
        invoicedQty: PropTypes.string,
      }),
    ).isRequired,
  }).isRequired,
};

export default memo(SoLineItemTable);
